import React, { Fragment, useState } from "react";
import NewToolTip from "../../../../common/New-ToolTip";
import UncodedAllergy from "../../../../common/tooltip/patient/uncoded-allergy";
import { ReactSVG } from "react-svg";
import { capitalizeFirstLetter, formatWordsIntoLines } from "../../../../../helpers/functions";
import { Menu, Transition } from "@headlessui/react";
import {
  categoryOptions,
  typeOptions,
  severityOptions,
} from "../../../clinical-services/service-steps/clinical-assessment/data";
import ConfirmationModalNew from "../../../../common/ConfirmationModalNew";
import { useDispatch } from "react-redux";
import { AllergyValueLabelOptions } from "../../../create/options";
import Button from "../../../../common/Button";
import { updateAllergy } from "../../../../../redux/modules/patient/actions";
import { useClickOutsideTooltip } from "../../../../../hooks/useClickOutsideTooltip";

const AllergyTable = ({
  label,
  allergies,
  allAllergies,
  tableFilter,
  isAllergyConfirmed,
  handleChange,
  checkAllAllergyConfirmed,
  confirmAllergies,
  setShowCreateSlider,
  setSelectedAllergy,
  setCreate,
  showConfirmedAllergy,
}) => {
  const dispatch = useDispatch();
  const [idToDelete, setIdToDelete] = useState(null);
  const [openDeleteAllergy, setOpenDeleteAllergy] = useState(false);
  const [openRestoreAllergy, setOpenRestoreAllergy] = useState(false);
  const [idToRestore, setIdToRestore] = useState(null);
  const [openTooltipId, setOpenTooltipId] = useState(null);
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const tooltipWrapperRef = useClickOutsideTooltip(openTooltipId, setOpenTooltipId);

  const restoreAllergy = (allergyId) => {
    const restoreObject = allAllergies.find((val) => val.id === allergyId);
    const previousStatus = restoreObject?.status;
    restoreObject.status = "active";
    restoreObject.confirmedDate = null;
    const { id, ...updatedObject } = restoreObject;
    updatedObject.previousStatus = previousStatus;
    dispatch(updateAllergy(id, updatedObject, false, 'restored'));
  };
  const deleteAllergy = (index) => {
    const deleteObject = allAllergies.find((val) => val.id === index);
    const previousStatus = deleteObject?.status;
    deleteObject.status = "inactive";
    const { id, ...updatedObject } = deleteObject;
    updatedObject.previousStatus = previousStatus;
    dispatch(updateAllergy(id, updatedObject, false, 'deleted'));
  };

  const getAllergyNameBasedOnId = (id) => {
    if (id !== null) {
      return capitalizeFirstLetter(
        allergies.find((val) => val.id === id)?.allergyName ?? ""
      );
    } else {
      return "";
    }
  };

  // Apply table filter once and reuse for rendering and scrollbar logic
  const filteredAllergies = allAllergies.filter((allergy) => {
    if (tableFilter === "active") {
      return allergy.status === "active";
    } else if (tableFilter === "inactive") {
      return allergy.status === "inactive";
    }
    return true; // 'all' or any other value
  });

  // Show internal scrollbar after 6 visible allergies
  const shouldShowScrollbar = filteredAllergies.length > 6;

  return (
    <div className="rounded-[6px] bg-white border border-gray-100 p-3">
      <h4 className="text-2xl font-bold text-teal-600 pb-2 pl-2">{label}</h4>
      <div className="rounded-[4px] border border-gray-100">
        <div
          className={`w-full ${ shouldShowScrollbar ? "overflow-y-auto scrollbar-hide max-h-[290px]" : ""}`}
        >
          <table className="min-w-full divide-y bg-gray-20">
          <thead className="bg-gray-20 items-start gap-4">
            <tr className="h-[56px]">
              <th
                scope="col"
                className="pl-6 w-[20%] text-body-md font-semibold text-left text-gray-900"
              >
                Allergy
              </th>
              <th
                scope="col"
                className="w-[9%] text-body-md font-semibold text-left text-gray-900"
              >
                Status
              </th>
              <th
                scope="col"
                className="w-[10%] text-body-md font-semibold text-left text-gray-900"
              >
                Category
              </th>
              <th
                scope="col"
                className="w-[11%] text-body-md font-semibold text-left text-gray-900"
              >
                Type
              </th>
              <th
                scope="col"
                className="w-[9%] text-body-md font-semibold text-left text-gray-900"
              >
                Reaction
              </th>
              <th
                scope="col"
                className="w-[9%] text-body-md font-semibold text-left text-gray-900"
              >
                Severity
              </th>
              <th
                scope="col"
                className="w-[9%] text-body-md font-semibold text-left text-gray-900"
              >
                Source
              </th>
              <th
                scope="col"
                className="w-[10%] text-body-md font-semibold text-center text-gray-900"
              >
                Comments
              </th>
              <th
                scope="col"
                className="w-[15%] text-body-md font-semibold text-center text-gray-900"
              >
                {showConfirmedAllergy &&
                !allergies.every((a) => a.status == "inactive") &&
                tableFilter !== "inactive" ? (
                  <Button
                    className="text-body-md rounded-[4px] !w-[202px] !h-full"
                    type={
                      checkAllAllergyConfirmed()
                        ? "primary"
                        : "primary-outline"
                    }
                    text={
                      checkAllAllergyConfirmed()
                        ? "Confirmed"
                        : "Confirm All"
                    }
                    onClick={
                      !checkAllAllergyConfirmed()
                        ? confirmAllergies
                        : undefined
                    }
                  />
                ) : (
                  <span>Actions</span>
                )}
              </th>
            </tr>
          </thead>
          <tbody className="min-w-full divide-y divide-gray-50 gap-4">
            {filteredAllergies.map((allergy, i) => {
              const isStripedRow = i % 2 === 1;
              const rowBgClass =
                allergy.status === "inactive" ? "bg-red-50" : isAllergyConfirmed(allergy.id) ? "bg-blue-100" : isStripedRow ? "bg-gray-10" : "bg-white";
              return (
                <tr
                  className={`${rowBgClass} transition-colors duration-300 m-4 cursor-pointer group `}
                  key={i}
                >
                  <td
                    className={`pl-6 py-2 w-[20%] text-body-md font-medium whitespace-nowrap ${
                      severityOptions.find(
                        (option) => option.value === allergy.severity
                      )?.label?.toLowerCase() === "severe"
                        ? "text-red-500"
                        : "text-gray-800"
                    }`}
                    data-tooltip-id={
                      allergy.allergyGenCode === null
                        ? "ma-uncoded-allergy"
                        : ""
                    }
                  >
                    <span className="capitalize">{allergy.allergyName}</span>

                    {allergy.allergyGenCode === null && (
                      <NewToolTip
                        id="ma-uncoded-allergy"
                        render={() => <UncodedAllergy />}
                      />
                    )}
                  </td>
                  <td className="w-[9%] text-body-md text-gray-800 whitespace-nowrap capitalize">
                    {allergy.status}
                  </td>
                  <td className="w-[10%] text-body-md text-gray-800 whitespace-nowrap capitalize">
                    {
                      categoryOptions.find(
                        (category) => category.value === allergy.category
                      )?.label
                    }
                  </td>
                  <td className="w-[11%] text-body-md text-gray-800 whitespace-nowrap capitalize">
                    {
                      typeOptions.find((type) => type.value === allergy.type)
                        ?.label
                    }
                  </td>
                  <td className="w-[14%] text-body-md text-gray-800 whitespace-nowrap capitalize">
                    {allergy.reaction}
                  </td>
                  <td
                    className={`w-[9%] text-body-md whitespace-nowrap capitalize ${
                      severityOptions.find(
                        (option) => option.value === allergy.severity
                      )?.label?.toLowerCase() === "severe"
                        ? "text-red-500"
                        : "text-gray-800"
                    }`}
                  >
                    {severityOptions.find(
                      (type) => type.value === allergy.severity
                    )?.label || allergy.severity}
                  </td>
                  <td className="w-[9%] text-body-md text-gray-800 whitespace-nowrap capitalize">
                    {allergy.source}
                  </td>
                  <td className="w-[10%] text-body-md text-gray-800 whitespace-nowrap">
                    {allergy?.comments?.length > 0 && (
                      <div className="flex w-full justify-center items-center pt-2">
                        <div
                          ref={openTooltipId === `allergyComments-${allergy.id}` ? tooltipWrapperRef : null}
                          className="relative inline-flex cursor-pointer"
                          data-tooltip-id={`allergyCommentsHover-${allergy.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            const tooltipId = `allergyComments-${allergy.id}`;
                            setOpenTooltipId(
                              openTooltipId === tooltipId ? null : tooltipId
                            );
                          }}
                        >
                          <ReactSVG
                            src="/icons/paper-clip-new.svg"
                            className="w-6"
                          />
                          {openTooltipId !== `allergyComments-${allergy.id}` && (
                            <NewToolTip
                              id={`allergyCommentsHover-${allergy.id}`}
                              render={() => (
                                <div className="bg-white p-2 text-teal-300 text-body-sm">
                                  Click to view comments
                                </div>
                              )}
                            />
                          )}
                          <NewToolTip
                            id={`allergyComments-${allergy.id}`}
                            isOpen={openTooltipId === `allergyComments-${allergy.id}`}
                            render={() => (
                              <div className="bg-white max-w-[460px]">
                                <div className="flex flex-col gap-3 p-2">
                                  <div className="text-teal-300 text-sm-body prose-a:underline">
                                    <div className="flex flex-col gap-4">
                                      <p
                                        style={{
                                          whiteSpace: "pre-wrap",
                                          wordBreak: "break-word",
                                        }}
                                      >
                                        {formatWordsIntoLines(allergy.comments)}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                            clickable={true}
                            handleHide={() => setOpenTooltipId(null)}
                            anchorSelect={`[data-tooltip-id="allergyCommentsHover-${allergy.id}"]`}
                          />
                        </div>
                      </div>
                    )}
                  </td>
                  <td className="w-[15%] text-body-md text-gray-800 whitespace-nowrap">
                    {allergy.status !== "inactive" && (
                      <div className={ showConfirmedAllergy ? "flex justify-between pl-16" : "flex items-center pl-4"}>
                        {showConfirmedAllergy && (
                          <input
                            type="checkbox"
                            className="ml-8 !border-gray-200"
                            checked={isAllergyConfirmed(allergy.id)}
                            id={i}
                            onChange={(e) => {
                              handleChange(e, allergy.id);
                            }}
                          />
                        )}
                        <div className={ showConfirmedAllergy ? "" : "flex flex-1 justify-center"}>
                          <Menu
                            as="div"
                            className="relative inline-block text-left"
                          >
                            {({ open }) => {
                              // Track which row's menu is open
                              if (open && openMenuIndex !== i) {
                                setOpenMenuIndex(i);
                              } else if (!open && openMenuIndex === i) {
                                setOpenMenuIndex(null);
                              }

                              // Hide icon if it's directly below an open menu (to avoid overlap)
                              const shouldHideIcon = openMenuIndex === i - 1;

                              return (
                                <>
                                  <div>
                                    <Menu.Button>
                                      <ReactSVG
                                        src={`/icons/triple-dot.svg`}
                                        className={`flex text-black !items-center w-6 ${
                                          shouldHideIcon
                                            ? "opacity-0 pointer-events-none"
                                            : ""
                                        }`}
                                      />
                                    </Menu.Button>
                                  </div>
                                  <Transition
                                    as={Fragment}
                                    enter="transition ease-out duration-100"
                                    enterFrom="transform translate-y-2 opacity-0 scale-95"
                                    enterTo="transform translate-y-0 opacity-100 scale-100"
                                    leave="transition ease-in duration-75"
                                    leaveFrom="transform translate-y-0 opacity-100 scale-100"
                                    leaveTo="transform translate-y-2 opacity-0 scale-95"
                                  >
                                    <Menu.Items
                                      className={
                                        i === filteredAllergies.length - 1 || filteredAllergies.length === 1
                                        ? "absolute right-full mr-[6px] min-w-[70px] w-auto origin-top-right bg-white divide-y divide-gray-20 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 top-0"
                                        : "absolute right-4 top-6 w-18 origin-top-right bg-white divide-y divide-gray-20 rounded-md shadow-lg ring-1 ring-black ring-opacity-5"
                                      }
                                    >
                                      <Menu.Item>
                                        <button
                                          className="block w-full text-left h-[30px] px-2.5 py-2.5 flex items-center border-b border-b-gray-20 hover:bg-gray-100"
                                          onClick={(e) => {
                                            setSelectedAllergy(allergy);
                                            setCreate(false);
                                            setShowCreateSlider(true);
                                          }}
                                        >
                                          Edit
                                        </button>
                                      </Menu.Item>
                                      <Menu.Item>
                                        <button
                                          className="block w-full text-left h-[30px] px-2.5 py-2.5 flex items-center border-b-gray-20 hover:bg-gray-100"
                                          onClick={() => {
                                            setOpenDeleteAllergy(true);
                                            setIdToDelete(allergy.id);
                                            setSelectedAllergy(allergy);
                                          }}
                                        >
                                          Delete
                                        </button>
                                      </Menu.Item>
                                    </Menu.Items>
                                  </Transition>
                                </>
                              );
                            }}
                          </Menu>
                        </div>
                      </div>
                    )}
                    {allergy.status === "inactive" && (
                      <div className="flex justify-end pl-16 mr-12">
                        <ReactSVG
                          src="/icons/undo.svg"
                          className="w-6"
                          onClick={() => {
                            setOpenRestoreAllergy(true);
                            setIdToRestore(allergy.id);
                            setSelectedAllergy(allergy);
                          }}
                        />
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
        <ConfirmationModalNew
          type="primary"
          title="Delete Allergy"
          content={() =>
            `Are you sure you want to delete ${getAllergyNameBasedOnId(
              idToDelete
            )}?`
          }
          className="w-[40%]"
          confirmAction={() => {
            deleteAllergy(idToDelete);
            setOpenDeleteAllergy(false);
            setIdToDelete(null);
          }}
          optionLabel="Reason"
          open={openDeleteAllergy}
          handleClose={() => {
            setOpenDeleteAllergy(false);
            setIdToDelete(null);
          }}
          onCross={() => {
            setOpenDeleteAllergy(false);
            setIdToDelete(null);
          }}
          options={AllergyValueLabelOptions}
        />
        <ConfirmationModalNew
          open={openRestoreAllergy}
          title={"Restore Allergy"}
          className="w-[40%]"
          type={"primary"}
          confirmText={"Yes"}
          // verticalButtons={true}
          content={() => (
            <p className="p-7">
              Are you sure you wish to restore{" "}
              {getAllergyNameBasedOnId(idToRestore)}?
            </p>
          )}
          onCross={() => {
            setOpenRestoreAllergy(false);
            setIdToRestore(null);
          }}
          handleClose={() => {
            setOpenRestoreAllergy(false);
            setIdToRestore(null);
          }}
          confirmAction={() => {
            restoreAllergy(idToRestore);
            setOpenRestoreAllergy(false);
            setIdToRestore(null);
          }}
        />
      </div>
    </div>
  );
};

export default AllergyTable;
